'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { $transcriptPanel, $aiChat, updateAIChatEvent } from '@nx-frontend/shared/store/transcript-panel';

export function TranscriptAiWelcome() {
  const [transcriptPanel, aiChat, updateAIChat] = useUnit([$transcriptPanel, $aiChat, updateAIChatEvent]);

  const t = useTranslations('features');

  const speakers = useMemo(() => {
    const speakers = [];
    for (const annotation of transcriptPanel.entities[transcriptPanel.attachment.globalId].annotations) {
      speakers.push(annotation.stt.speaker);
    }
    return [...new Set(speakers)];
  }, [transcriptPanel]);

  const handleCommand = useCallback(
    ({ cmd }: { cmd: string }) => {
      if ('done' === aiChat.state) {
        updateAIChat({
          cmd,
        });
      }
    },
    [aiChat.state, updateAIChat]
  );

  return (
    <div
      className="flex flex-col items-center w-[417px] h-[470px] p-[40px_24px] gap-[32px] flex-shrink-0 rounded-[8px] border border-[#E5E5E5]"
      style={{
        background:
          'linear-gradient(180deg, rgba(24, 159, 181, 1) -104.36%, #F8F8F8 28.65%, #FAFAFA 49.63%, #FFF 105.11%)',
      }}
    >
      <div>
        <Icon icon={IconsMap.MAGIC_2} className="w-[36px] h-[36px] text-white" />
      </div>
      <div
        className="text-center text-[16px] font-bold leading-[19.52px] mt-[-20px] "
        style={{
          color: 'var(--Light_Pr-FG-Text, rgba(0, 0, 0, 0.87))',
        }}
      >
        {t('transcript.welcome_to_fusebase_ai')}
      </div>
      <div className="w-[330px] mt-[-20px] text-center text-[13px] leading-[18.46px]">
        {t('transcript.your_video_has_been_transcribed')}
      </div>
      <div className="w-[369px]">
        <button
          className="flex flex-row items-center justify-start self-stretch h-[40px] p-[12px_16px_12px_12px] gap-[8px] rounded-[6px] w-full bg-white border border-[#E5E5E5] text-[13px] leading-[18.46px] hover:bg-[#F5F5F5]"
          onClick={() => handleCommand({ cmd: 'IMPROVE' })}
        >
          <Icon icon={IconsMap.BULB_2} className="w-[20px] h-[20px] text-white" />
          {t('transcript.suggest_improvements')}
        </button>
        {1 < speakers.length ? (
          <>
            <button
              className="flex flex-row items-center justify-start self-stretch h-[40px] p-[12px_16px_12px_12px] gap-[8px] mt-[8px] rounded-[6px] w-full bg-white border border-[#E5E5E5] text-[13px] leading-[18.46px] hover:bg-[#F5F5F5]"
              onClick={() => handleCommand({ cmd: `FOLLOW_UP<|>${speakers[0]}<|>${speakers[1]}` })}
            >
              <Icon icon={IconsMap.BULB_2} className="w-[20px] h-[20px] text-white" />
              {t('transcript.write_follow_up_email', { speaker: speakers[0] })}
            </button>
            <button
              className="flex flex-row items-center justify-start self-stretch h-[40px] p-[12px_16px_12px_12px] gap-[8px] mt-[8px] rounded-[6px] w-full bg-white border border-[#E5E5E5] text-[13px] leading-[18.46px] hover:bg-[#F5F5F5]"
              onClick={() => handleCommand({ cmd: `FOLLOW_UP<|>${speakers[1]}<|>${speakers[0]}` })}
            >
              <Icon icon={IconsMap.BULB_2} className="w-[20px] h-[20px] text-white" />
              {t('transcript.write_follow_up_email', { speaker: speakers[1] })}
            </button>
          </>
        ) : null}
        <button
          className="flex flex-row items-center justify-start self-stretch h-[40px] p-[12px_16px_12px_12px] gap-[8px] mt-[8px] rounded-[6px] w-full bg-white border border-[#E5E5E5] text-[13px] leading-[18.46px] hover:bg-[#F5F5F5]"
          onClick={() => handleCommand({ cmd: `CTA` })}
        >
          <Icon icon={IconsMap.BULB_2} className="w-[20px] h-[20px] text-white" />
          {t('transcript.create_action_items')}
        </button>
      </div>
      <div className="text-center text-[13px] leading-[18.46px]">{t('transcript.or_use_new_queries')}</div>
    </div>
  );
}
